import { DismissibleTag, Inline, Stack } from 'canoe';
import { createCacheKey, ENDPOINTS, fetchResource, useCreatePayload } from 'hammock';
import { nanoid } from 'nanoid';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useDispatch } from 'react-redux';

import { SEGMENT_DEFAULT_ID } from '@candu-ai/segments/types';
import {
  getNameDifferentiator,
  stripNameDifferentiator,
} from 'dashboard/pages/Segment/components/DefinitionForm/nameDifferentiatorUtils';

import { FilterForm } from './FilterForm';
import { getFiltersTag } from './getFiltersTag';
import {
  setEditingFilter,
  setFilters as setFiltersAction,
  setToggleIsAddingFilter,
  setWhitelistedSegmentIds,
  useEditingFilter,
  useFilters,
  useIsAddingFilter,
} from './state';
import { defaultFilterOptions, Filter, FilterTypes, GenericFilter, UserPropertyFilter } from './types';
import { isUserPropertyFilter } from './utils';
import { withAnalyticsSearch } from './withAnalyticsSearch';

interface Props {
  availableFilters?: FilterTypes[];
  whitelistedSegmentIds?: string[];
}

const FiltersImpl = ({ availableFilters = defaultFilterOptions, whitelistedSegmentIds }: Props) => {
  const dispatch = useDispatch();
  const setReduxFilters = (filters: Filter[]) => dispatch(setFiltersAction(filters));
  const filters = useFilters();

  const { getAll: getSegments } = useCreatePayload(ENDPOINTS.segmentsDefinition);
  const { getAll: getContents } = useCreatePayload(ENDPOINTS.contentMetadata);

  const isAddingFilter = useIsAddingFilter();
  const editingFilter = useEditingFilter();

  const payloadSegments = getSegments({});
  const payloadContents = getContents({});
  const segmentsCacheKey = createCacheKey(payloadSegments);
  const contentCacheKey = createCacheKey(payloadContents);

  const { data: contents } = useQuery(contentCacheKey, () => fetchResource(payloadContents)) as any;
  const { data: segmentsData } = useQuery(segmentsCacheKey, () =>
    fetchResource(payloadSegments),
  ) as any;

  useEffect(() => {
    dispatch(setWhitelistedSegmentIds(whitelistedSegmentIds));
  }, [whitelistedSegmentIds]);

  const segments = useMemo(() => {
    return [
      {
        id: SEGMENT_DEFAULT_ID,
        name: SEGMENT_DEFAULT_ID,
      },
      ...(segmentsData || []),
    ];
  }, [segmentsData, whitelistedSegmentIds]);

  const getFilterTag = useCallback((filter: Filter) => getFiltersTag(filter, contents, segments), [
    contents,
    segments,
  ]);

  const setFilters = (newFilters: Filter[]) => {
    setReduxFilters(newFilters);
  };

  const onSaveNewFilter = (newFilter: GenericFilter | UserPropertyFilter) => {
    if (isUserPropertyFilter(newFilter)) {
      setFilters([
        ...filters,
        {
          ...newFilter,
          traitName: stripNameDifferentiator(newFilter.traitName),
          traitType: getNameDifferentiator(newFilter.traitName),
          id: nanoid(6),
        },
      ]);
    } else {
      setFilters([...filters, { ...newFilter, id: nanoid(6) }]);
    }
    dispatch(setToggleIsAddingFilter());
  };

  const onUpdateFilter = (newFilter: GenericFilter | UserPropertyFilter) => {
    setFilters(
      filters.map((f) => {
        if (f.id === editingFilter?.id) {
          if (isUserPropertyFilter(newFilter)) {
            return {
              ...newFilter,
              traitName: stripNameDifferentiator(newFilter.traitName),
              traitType: getNameDifferentiator(newFilter.traitName),
              id: nanoid(6),
            };
          }
          return newFilter;
        }
        return f;
      }),
    );
    dispatch(setEditingFilter());
  };

  return (
    <Stack space="s" spaceAfter="normal">
      {isAddingFilter && (
        <FilterForm
          key="new-filter-form"
          availableFilters={availableFilters}
          onSave={onSaveNewFilter}
          onRemove={() => dispatch(setToggleIsAddingFilter())}
        />
      )}
      {editingFilter && (
        <FilterForm
          key={`${editingFilter.id}-form`}
          availableFilters={availableFilters}
          onSave={onUpdateFilter}
          onCancel={() => dispatch(setEditingFilter())}
          onRemove={() => {
            setFilters(filters.filter((f) => f.id !== editingFilter.id));
            dispatch(setEditingFilter());
          }}
          filter={
            isUserPropertyFilter(editingFilter)
              ? {
                  ...editingFilter,
                  traitName: `${editingFilter.traitName}_differentiator(${editingFilter.traitType})`,
                  traitType: undefined,
                }
              : editingFilter
          }
        />
      )}
      {filters.length > 0 && (
        <Inline space="xs" wrap="wrap" align="center" mt="xs">
          {filters
            .filter((f) => f.id !== editingFilter?.id)
            .map((filter) => (
              <DismissibleTag
                key={`${filter.id}-tag`}
                variant="secondary"
                onClick={() => dispatch(setEditingFilter(filter))}
                onDismiss={() => setFilters(filters.filter((f) => f.id !== filter.id))}
                text={getFilterTag(filter)}
                size="default"
                strong
                limitWidth
              />
            ))}
        </Inline>
      )}
    </Stack>
  );
};

export const Filters = withAnalyticsSearch(FiltersImpl);
